// src/pages/MyDailyFoodBookings.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import "../styles/MyDailyFoodBookings.css";
import { useAuth } from "../context/AuthContext";
import BookingPageShell from "../components/BookingPageShell";

import { getApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  limit,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/* =========================
   CONFIG
   ========================= */

const ADMIN_EMAILS = [
  "hariharanmadhav@gmail.com",
  "mugiwaraaluffy007@gmail.com",
  "lalithaganesan449@gmail.com",
];

const BOOKINGS_COLLECTION = "dailyFoodBookings";
const USERS_COLLECTION = "users";
const BOOKING_TYPE_VALUE = "Cloud Kitchen";

const STATUS_LIST = [
  "pending",
  "accepted",
  "confirmed",
  "completed",
  "cancelled",
];

// 👉 Pagination size
const PAGE_SIZE = 10;

/* =========================
   UTILITIES
   ========================= */

const normalize = (v) =>
  (v || "").toString().toLowerCase().trim();

const safeDate = (val) => {
  try {
    if (!val) return "Unknown";
    if (typeof val?.toDate === "function") {
      return val.toDate().toLocaleString();
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? "Unknown" : d.toLocaleString();
  } catch {
    return "Unknown";
  }
};

/* =========================
   SMALL COMPONENTS
   ========================= */

const StatusPill = React.memo(({ status }) => {
  const s = normalize(status || "pending");
  return <span className={`status-pill status-${s}`}>{s}</span>;
});
StatusPill.propTypes = { status: PropTypes.string };

const LoadingScreen = React.memo(() => (
  <div className="my-dailyfood-page">
    <h2>Loading Daily Food Orders…</h2>
  </div>
));

const ErrorScreen = React.memo(({ message, onRetry }) => (
  <div className="my-dailyfood-page">
    <h2>Error</h2>
    <p>{message}</p>
    <button className="btn" onClick={onRetry}>
      Retry
    </button>
  </div>
));
ErrorScreen.propTypes = {
  message: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

/* =========================
   CARD
   ========================= */

const DailyFoodCard = React.memo(
  ({ booking, isAdmin, onStatusChange, statusUpdating }) => {
    const handleChange = useCallback(
      (e) => onStatusChange(booking.id, e.target.value),
      [booking.id, onStatusChange]
    );

    return (
      <article className="df-card">
        <div className="df-card-header">
          <h3>{booking.menu || "Cloud Kitchen Order"}</h3>
          <StatusPill status={booking.status} />
        </div>

        <div className="df-card-body">
          <p><b>Date:</b> {booking.date || "—"}</p>
          <p><b>Time:</b> {booking.time || "—"}</p>
          <p><b>Guests:</b> {booking.guests || "—"}</p>
          <p><b>Venue:</b> {booking.venue || "—"}</p>
          <p><b>Email:</b> {booking.userEmail || "—"}</p>
        </div>

        <div className="df-card-footer">
          <div>Ordered on: {safeDate(booking.createdAt)}</div>

          {isAdmin && (
            <div className="admin-controls">
              <label>Status</label>
              <select
                value={normalize(booking.status || "pending")}
                disabled={statusUpdating}
                onChange={handleChange}
              >
                {STATUS_LIST.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {statusUpdating && (
                <span className="saving">Saving…</span>
              )}
            </div>
          )}
        </div>
      </article>
    );
  }
);

DailyFoodCard.propTypes = {
  booking: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  statusUpdating: PropTypes.bool.isRequired,
};

/* =========================
   MAIN PAGE
   ========================= */

const MyDailyFoodBookings = () => {
  const { currentUser, loading: authLoading } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [statusUpdatingMap, setStatusUpdatingMap] = useState({});
  const [isAdminByRole, setIsAdminByRole] = useState(false);

  // 👉 Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  const mountedRef = useRef(false);
  const unsubscribeRef = useRef(null);

  const db = useMemo(() => getFirestore(getApp()), []);

  const isAdminByEmail = useMemo(() => {
    if (!currentUser?.email) return false;
    return ADMIN_EMAILS.includes(currentUser.email.toLowerCase());
  }, [currentUser]);

  const isAdmin = isAdminByRole || isAdminByEmail;

  /* ===== Mount Guard ===== */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  /* ===== Role Listener ===== */
  useEffect(() => {
    if (!currentUser) return;

    const userRef = doc(db, USERS_COLLECTION, currentUser.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      if (!mountedRef.current) return;
      setIsAdminByRole(!!snap.data()?.isAdmin);
    });

    return () => unsub();
  }, [currentUser, db]);

  /* ===== Firestore Listener ===== */
  const startListener = useCallback(() => {
    if (!currentUser && !isAdmin) {
      setLoading(false);
      return;
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const colRef = collection(db, BOOKINGS_COLLECTION);

    const q = isAdmin
      ? query(
          colRef,
          where("bookingType", "==", BOOKING_TYPE_VALUE),
          limit(200)
        )
      : query(
          colRef,
          where("bookingType", "==", BOOKING_TYPE_VALUE),
          where("userId", "==", currentUser.uid),
          limit(100)
        );

    unsubscribeRef.current = onSnapshot(
      q,
      (snap) => {
        if (!mountedRef.current) return;

        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ta = a.createdAt?.toDate?.()?.getTime?.() || 0;
            const tb = b.createdAt?.toDate?.()?.getTime?.() || 0;
            return tb - ta;
          });

        setBookings(docs);
        setLoading(false);
        setError("");
      },
      (err) => {
        if (!mountedRef.current) return;
        setError(err.message || "Failed to load bookings");
        setLoading(false);
      }
    );
  }, [currentUser, isAdmin, db]);

  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    setError("");
    startListener();
  }, [authLoading, startListener]);

  /* ===== Derived ===== */
  const visibleBookings = useMemo(() => {
    if (filterStatus === "all") return bookings;
    return bookings.filter(
      (b) => normalize(b.status) === normalize(filterStatus)
    );
  }, [bookings, filterStatus]);

  // 👉 Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  // 👉 Pagination calculations
  const totalPages = Math.max(
    1,
    Math.ceil(visibleBookings.length / PAGE_SIZE)
  );

  const paginatedBookings = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return visibleBookings.slice(start, end);
  }, [visibleBookings, currentPage]);

  /* ===== Handlers ===== */
  const handleStatusChange = useCallback(
    async (bookingId, newStatus) => {
      if (!isAdmin) return;

      setStatusUpdatingMap((m) => ({ ...m, [bookingId]: true }));

      try {
        const ref = doc(db, BOOKINGS_COLLECTION, bookingId);
        await updateDoc(ref, {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
      } catch {
        alert("Failed to update status");
      } finally {
        setStatusUpdatingMap((m) => {
          const n = { ...m };
          delete n[bookingId];
          return n;
        });
      }
    },
    [db, isAdmin]
  );

  /* ===== Screens ===== */
  if (authLoading || loading) return <LoadingScreen />;

  if (error) {
    return (
      <ErrorScreen
        message={error}
        onRetry={startListener}
      />
    );
  }

  return (
    <BookingPageShell
      title={isAdmin ? "All Daily Food Orders" : "My Daily Food Orders"}
      isAdmin={isAdmin}
    >
      <main className="my-dailyfood-page">
        <header className="df-header">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All</option>
            {STATUS_LIST.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </header>

        {paginatedBookings.length === 0 ? (
          <p>No Daily Food Orders</p>
        ) : (
          <>
            <section className="df-grid">
              {paginatedBookings.map((b) => (
                <DailyFoodCard
                  key={b.id}
                  booking={b}
                  isAdmin={isAdmin}
                  onStatusChange={handleStatusChange}
                  statusUpdating={!!statusUpdatingMap[b.id]}
                />
              ))}
            </section>

            {/* ===== Pagination Controls ===== */}
            {totalPages > 1 && (
              <div className="df-pagination">
                <button
                  className="btn"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((p) => Math.max(1, p - 1))
                  }
                >
                  ⬅ Previous
                </button>

                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  className="btn"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(totalPages, p + 1)
                    )
                  }
                >
                  Next ➡
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </BookingPageShell>
  );
};

export default MyDailyFoodBookings;
